import {
	faChevronDown,
	faChevronRight,
	faCog,
	faExclamationTriangle,
	faSearch,
	faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	AiAgentToolsQueryService,
	type AiAgentTool,
} from '@supply-mover-2-0/core';
import type { AgentConfigSchema, ToolAudience } from '@supply-mover-2-0/shared';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Text } from '../../components/base/Text';
import { LoadingSpinner } from '../../components/display/LoadingSpinner';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { AgentToolConfigField } from './AgentToolConfigField';

// Re-export the type for consumers
export type { AiAgentTool };

// Alias for backward compatibility within this file
type Tool = AiAgentTool;

export interface SelectedTool {
	toolId: number;
	type?: string;
	name?: string;
	config?: Record<string, unknown>;
	/**
	 * Agent config schema from tool metadata (for validation purposes)
	 */
	agentConfigSchema?: AgentConfigSchema;
}

interface AgentToolSelectorProps {
	/**
	 * Available tools to display. If not provided, tools will be fetched automatically.
	 */
	availableTools?: Array<Tool>;
	selectedTools: Array<SelectedTool>;
	onSelectionChange: (tools: Array<SelectedTool>) => void;
	/**
	 * Whether tools are being loaded externally. Only used when availableTools is provided.
	 */
	isLoading?: boolean;
	/**
	 * PD-16920 — offer only the tools that serve this audience (who the agent
	 * is for). Applies when the selector fetches its own tools; omit or pass
	 * null to list every tool.
	 */
	audience?: ToolAudience | null;
}

const PAGE_SIZE = 50;
const SCROLL_THRESHOLD = 100;

const PRESET_ORDER = [
	'common',
	'orderEntry',
	'sales',
	'customerService',
	'collections',
	'analytics',
	'tasks',
	'telephony',
	'communication',
	'purchasing',
];

const PRESET_LABELS: Record<string, string> = {
	common: 'Common Tools',
	orderEntry: 'Order Entry',
	sales: 'Sales',
	customerService: 'Customer Service',
	collections: 'Collections',
	analytics: 'Analytics',
	telephony: 'Telephony',
	communication: 'Communication',
	purchasing: 'Purchasing',
	tasks: 'Tasks',
	other: 'Other',
};

/**
 * Get agentConfig schema from tool metadata
 */
export function getAgentConfig(tool: Tool): AgentConfigSchema | undefined {
	return tool.metadata?.['agentConfig'] as AgentConfigSchema | undefined;
}

/**
 * Check if a tool has required configuration that is not yet filled
 */
export function hasUnfilledRequiredConfig(
	tool: Tool,
	config?: Record<string, unknown>,
): boolean {
	const agentConfig = getAgentConfig(tool);

	if (agentConfig == null) {
		return false;
	}

	return Object.entries(agentConfig).some(([fieldName, fieldDef]) => {
		if (!fieldDef.required) {
			return false;
		}

		const value = config?.[fieldName];

		return value == null || value === '';
	});
}

/**
 * Check if a SelectedTool has required configuration that is not yet filled.
 * Works directly with SelectedTool without needing to look up the full tool.
 */
export function selectedToolHasUnfilledConfig(
	selectedTool: SelectedTool,
): boolean {
	const agentConfig = selectedTool.agentConfigSchema;

	if (agentConfig == null) {
		return false;
	}

	return Object.entries(agentConfig).some(([fieldName, fieldDef]) => {
		if (!fieldDef.required) {
			return false;
		}

		const value = selectedTool.config?.[fieldName];

		return value == null || value === '';
	});
}

export const AgentToolSelector: React.FC<AgentToolSelectorProps> = ({
	availableTools: externalTools,
	selectedTools,
	onSelectionChange,
	isLoading: externalLoading = false,
	audience = null,
}) => {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [expandedPresets, setExpandedPresets] = useState<Set<string>>(
		new Set([...PRESET_ORDER, 'other']),
	);
	const [configuringToolId, setConfiguringToolId] = useState<number | null>(
		null,
	);

	// Self-fetching state (used when externalTools is not provided)
	const [fetchedTools, setFetchedTools] = useState<Array<Tool>>([]);
	const [isFetching, setIsFetching] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Determine if we're in self-fetching mode
	const isSelfFetching = externalTools == null;
	const availableTools = isSelfFetching ? fetchedTools : externalTools;
	const isLoading = isSelfFetching ? isFetching : externalLoading;

	// Debounce search input
	useEffect(() => {
		if (searchTimeoutRef.current != null) {
			clearTimeout(searchTimeoutRef.current);
		}

		searchTimeoutRef.current = setTimeout(() => {
			setDebouncedSearch(searchTerm);
		}, 300);

		return () => {
			if (searchTimeoutRef.current != null) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, [searchTerm]);

	// Fetch tools when in self-fetching mode
	const fetchTools = useCallback(
		async (page: number, search: string, append: boolean) => {
			if (!isSelfFetching) {
				return;
			}

			if (page === 1) {
				setIsFetching(true);
			} else {
				setIsLoadingMore(true);
			}

			try {
				const result = await AiAgentToolsQueryService.getTools({
					page,
					pageSize: PAGE_SIZE,
					search: search.trim() !== '' ? search.trim() : undefined,
					filters: audience != null ? { audience } : undefined,
				});

				if (result.success) {
					if (append) {
						setFetchedTools((prev) => [...prev, ...result.data]);
					} else {
						setFetchedTools(result.data);
					}

					// Check if there are more pages
					const pagination = result.pagination;
					if (pagination != null) {
						setHasMore(pagination.page < pagination.totalPages);
					} else {
						// If no pagination info, assume no more if we got fewer than PAGE_SIZE
						setHasMore(result.data.length >= PAGE_SIZE);
					}

					setCurrentPage(page);
				}
			} catch (error) {
				console.error('Failed to fetch tools:', error);
			} finally {
				setIsFetching(false);
				setIsLoadingMore(false);
			}
		},
		[isSelfFetching, audience],
	);

	// Initial fetch and search changes
	useEffect(() => {
		if (isSelfFetching) {
			setCurrentPage(1);
			setHasMore(true);
			fetchTools(1, debouncedSearch, false);
		}
	}, [isSelfFetching, debouncedSearch, fetchTools]);

	// Handle scroll for infinite loading
	const handleScroll = useCallback(() => {
		if (!isSelfFetching || !hasMore || isLoadingMore || isFetching) {
			return;
		}

		const container = scrollContainerRef.current;
		if (container == null) {
			return;
		}

		const { scrollTop, scrollHeight, clientHeight } = container;

		if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD) {
			fetchTools(currentPage + 1, debouncedSearch, true);
		}
	}, [
		isSelfFetching,
		hasMore,
		isLoadingMore,
		isFetching,
		currentPage,
		debouncedSearch,
		fetchTools,
	]);

	// Create a map of selected tool IDs to their configs
	const selectedToolsMap = useMemo(() => {
		const map = new Map<number, Record<string, unknown> | undefined>();

		selectedTools.forEach((st) => {
			map.set(st.toolId, st.config);
		});

		return map;
	}, [selectedTools]);

	// Get selected tool IDs for easy checking
	const selectedToolIds = useMemo(
		() => selectedTools.map((st) => st.toolId),
		[selectedTools],
	);

	// Group tools by preset and sort alphabetically
	const toolsByPreset = useMemo(() => {
		const groups: Record<string, Array<Tool>> = {};

		availableTools.forEach((tool) => {
			const presets = (tool.metadata?.['presets'] as
				| Array<string>
				| undefined) ?? ['other'];

			presets.forEach((preset) => {
				if (groups[preset] == null) {
					groups[preset] = [];
				}

				groups[preset].push(tool);
			});
		});

		// Sort tools within each preset alphabetically by name
		Object.keys(groups).forEach((preset) => {
			const presetGroup = groups[preset];
			if (presetGroup != null) {
				presetGroup.sort((a, b) => a.name.localeCompare(b.name));
			}
		});

		// Sort presets by order, then alphabetically for any not in PRESET_ORDER
		const sortedGroups: Record<string, Array<Tool>> = {};
		const orderedPresets = [...PRESET_ORDER, 'other'];

		orderedPresets.forEach((preset) => {
			if (groups[preset] != null) {
				sortedGroups[preset] = groups[preset];
			}
		});

		// Add any presets not in PRESET_ORDER, sorted alphabetically
		const remainingPresets = Object.keys(groups)
			.filter((preset) => !orderedPresets.includes(preset))
			.sort((a, b) => a.localeCompare(b));

		remainingPresets.forEach((preset) => {
			const presetGroup = groups[preset];
			if (presetGroup != null) {
				sortedGroups[preset] = presetGroup;
			}
		});

		return sortedGroups;
	}, [availableTools]);

	// Check if search is pending (user typed but API call not yet made)
	const isSearchPending = isSelfFetching && searchTerm !== debouncedSearch;

	// Filter tools by search term (maintains alphabetical sort)
	// When self-fetching, the API handles search, so skip client-side filtering
	const filteredToolsByPreset = useMemo(() => {
		// In self-fetching mode, API already returns filtered results
		if (isSelfFetching) {
			return toolsByPreset;
		}

		// Client-side filtering for externally provided tools
		if (searchTerm.trim() === '') {
			return toolsByPreset;
		}

		const term = searchTerm.toLowerCase();
		const filtered: Record<string, Array<Tool>> = {};

		Object.entries(toolsByPreset).forEach(([preset, presetTools]) => {
			const matchingTools = presetTools.filter(
				(tool) =>
					tool.name.toLowerCase().includes(term) ||
					(tool.type ?? '').toLowerCase().includes(term) ||
					(tool.description ?? '').toLowerCase().includes(term),
			);

			if (matchingTools.length > 0) {
				filtered[preset] = matchingTools.sort((a, b) =>
					a.name.localeCompare(b.name),
				);
			}
		});

		return filtered;
	}, [toolsByPreset, searchTerm, isSelfFetching]);

	// Find the tool being configured
	const configuringTool = useMemo(() => {
		if (configuringToolId == null) {
			return null;
		}

		return availableTools.find((t) => t.id === configuringToolId) ?? null;
	}, [configuringToolId, availableTools]);

	// Handlers
	const handleToggleTool = (tool: Tool) => {
		const isSelected = selectedToolIds.includes(tool.id);

		if (isSelected) {
			// Remove tool
			onSelectionChange(selectedTools.filter((st) => st.toolId !== tool.id));
			// Close config panel if this tool was being configured
			if (configuringToolId === tool.id) {
				setConfiguringToolId(null);
			}
		} else {
			// Add tool - include type, name, and config schema for parent validation
			const agentConfig = getAgentConfig(tool);

			if (agentConfig != null) {
				// Tool requires configuration - add it and open config panel
				onSelectionChange([
					...selectedTools,
					{
						toolId: tool.id,
						type: tool.type,
						name: tool.name,
						config: {},
						agentConfigSchema: agentConfig,
					},
				]);
				setConfiguringToolId(tool.id);
			} else {
				// No configuration required - just add it
				onSelectionChange([
					...selectedTools,
					{ toolId: tool.id, type: tool.type, name: tool.name },
				]);
			}
		}
	};

	const handleConfigChange = (
		toolId: number,
		fieldName: string,
		value: unknown,
	) => {
		const newTools = selectedTools.map((st) => {
			if (st.toolId === toolId) {
				return {
					...st,
					config: {
						...(st.config ?? {}),
						[fieldName]: value,
					},
				};
			}

			return st;
		});

		onSelectionChange(newTools);
	};

	const handleSelectAllPreset = (preset: string, event: React.MouseEvent) => {
		event.stopPropagation();
		const presetTools = filteredToolsByPreset[preset] ?? [];
		const presetToolIds = presetTools.map((tool) => tool.id);
		const allSelected = presetToolIds.every((id) =>
			selectedToolIds.includes(id),
		);

		if (allSelected) {
			// Deselect all in preset
			onSelectionChange(
				selectedTools.filter((st) => !presetToolIds.includes(st.toolId)),
			);
		} else {
			// Select all in preset (preserve existing configs)
			const newTools = [...selectedTools];

			presetTools.forEach((tool) => {
				if (!selectedToolIds.includes(tool.id)) {
					const agentConfig = getAgentConfig(tool);
					newTools.push({
						toolId: tool.id,
						type: tool.type,
						name: tool.name,
						config: {},
						...(agentConfig != null && { agentConfigSchema: agentConfig }),
					});
				}
			});
			onSelectionChange(newTools);
		}
	};

	const togglePresetExpanded = (preset: string) => {
		const newExpanded = new Set(expandedPresets);

		if (newExpanded.has(preset)) {
			newExpanded.delete(preset);
		} else {
			newExpanded.add(preset);
		}

		setExpandedPresets(newExpanded);
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center p-8">
				<LoadingSpinner />
				<Text className={cn('ml-2', colors.classes.textSecondary)}>
					{t('Loading tools...')}
				</Text>
			</div>
		);
	}

	return (
		<div className="flex h-full overflow-hidden">
			{/* Left Panel: Tool Selection */}
			<div
				className={cn(
					'flex flex-col min-w-0 overflow-hidden',
					configuringTool != null ? 'w-1/2' : 'w-full',
					'border-r',
					colors.classes.borderDefault,
				)}
			>
				{/* Search */}
				<div className={cn('p-3 border-b', colors.classes.borderDefault)}>
					<div className="relative">
						<FontAwesomeIcon
							icon={isSearchPending ? faSpinner : faSearch}
							className={cn(
								'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4',
								colors.classes.textSecondary,
								isSearchPending && 'animate-spin',
							)}
						/>
						<input
							type="text"
							placeholder={t('Search tools...')}
							value={searchTerm}
							onChange={(e) => {
								setSearchTerm(e.target.value);
							}}
							className={cn(
								'w-full has-icon-left pl-10 pr-3 py-2 border rounded-md text-sm',
								colors.classes.borderDefault,
								colors.classes.bgPrimary,
								colors.classes.textPrimary,
								'focus:outline-none focus:ring-2 focus:ring-accent',
							)}
						/>
					</div>
				</div>

				{/* Tool list by preset */}
				<div
					ref={scrollContainerRef}
					className="flex-1 overflow-y-auto overflow-x-hidden"
					onScroll={handleScroll}
				>
					{Object.entries(filteredToolsByPreset).length === 0 && (
						<div className="p-4 text-center">
							<Text className={colors.classes.textSecondary}>
								{t('No tools found')}
							</Text>
						</div>
					)}

					{Object.entries(filteredToolsByPreset).map(
						([preset, presetTools]) => {
							const presetToolIds = presetTools.map((tool) => tool.id);
							const selectedCount = presetToolIds.filter((id) =>
								selectedToolIds.includes(id),
							).length;
							const isExpanded = expandedPresets.has(preset);

							return (
								<div
									key={preset}
									className={cn('border-b', colors.classes.borderDefault)}
								>
									{/* Preset header */}
									<div
										className={cn(
											'flex items-center justify-between px-4 py-3 cursor-pointer',
											colors.classes.bgSecondary,
											'hover:opacity-80',
										)}
										onClick={() => {
											togglePresetExpanded(preset);
										}}
									>
										<div className="flex items-center gap-2">
											<FontAwesomeIcon
												icon={isExpanded ? faChevronDown : faChevronRight}
												className={cn('w-3 h-3', colors.classes.textSecondary)}
											/>
											<Text
												weight="medium"
												className={colors.classes.textPrimary}
											>
												{t(PRESET_LABELS[preset] ?? preset)}
											</Text>
											<Text size="sm" className={colors.classes.textSecondary}>
												({selectedCount}/{presetTools.length})
											</Text>
										</div>
										<button
											type="button"
											onClick={(e) => {
												handleSelectAllPreset(preset, e);
											}}
											className="text-sm text-accent hover:text-accent"
										>
											{selectedCount === presetTools.length
												? t('Deselect All')
												: t('Select All')}
										</button>
									</div>

									{/* Tools in preset */}
									{isExpanded && (
										<div
											className={cn('divide-y', colors.classes.borderDefault)}
										>
											{presetTools.map((tool) => {
												const isSelected = selectedToolIds.includes(tool.id);
												const hasConfig = getAgentConfig(tool) != null;
												const currentConfig = selectedToolsMap.get(tool.id);
												const needsConfig =
													isSelected &&
													hasUnfilledRequiredConfig(tool, currentConfig);
												const isConfiguring = configuringToolId === tool.id;

												return (
													<div
														key={tool.id}
														className={cn(
															'flex items-start gap-3 px-4 py-3 overflow-hidden',
															colors.classes.bgPrimary,
															'hover:opacity-90',
															isConfiguring && colors.classes.calloutInfo,
														)}
													>
														<input
															type="checkbox"
															checked={isSelected}
															onChange={() => {
																handleToggleTool(tool);
															}}
															className="mt-1 cursor-pointer"
														/>
														<div
															className="flex-1 min-w-0 cursor-pointer"
															onClick={() => {
																handleToggleTool(tool);
															}}
														>
															<div className="flex items-center gap-2 flex-wrap">
																<Text
																	weight="medium"
																	className={cn(
																		'truncate',
																		colors.classes.textPrimary,
																	)}
																>
																	{t(tool.name)}
																</Text>
																{(tool as Tool & { isDefault?: boolean })
																	.isDefault === true && (
																	<span
																		className={cn(
																			'px-1.5 py-0.5 text-xs text-accent rounded shrink-0',
																			colors.classes.badgeInfo,
																		)}
																	>
																		{t('Default')}
																	</span>
																)}
																{hasConfig && (
																	<span className="px-1.5 py-0.5 text-xs rounded shrink-0 bg-chip-4 text-chip-4 border border-chip-4">
																		{t('Configurable')}
																	</span>
																)}
																{needsConfig && (
																	<FontAwesomeIcon
																		icon={faExclamationTriangle}
																		className="text-warning"
																		title={t('Configuration required')}
																	/>
																)}
															</div>
															{/* Show humanReadableDescription from metadata if available */}
															{tool.metadata?.['humanReadableDescription'] !=
																null &&
																tool.metadata['humanReadableDescription'] !==
																	'' && (
																	<Text
																		size="sm"
																		className={colors.classes.textSecondary}
																	>
																		{t(
																			String(
																				tool.metadata[
																					'humanReadableDescription'
																				],
																			),
																		)}
																	</Text>
																)}
														</div>
														{/* Configure button */}
														{hasConfig && isSelected && (
															<button
																type="button"
																onClick={(e) => {
																	e.stopPropagation();
																	setConfiguringToolId(
																		isConfiguring ? null : tool.id,
																	);
																}}
																className={cn(
																	'p-1 rounded hover:bg-control',
																	isConfiguring && colors.classes.calloutInfo,
																)}
																title={t('Configure tool')}
															>
																<FontAwesomeIcon
																	icon={faCog}
																	className={cn(
																		isConfiguring
																			? 'text-accent'
																			: colors.classes.textSecondary,
																	)}
																/>
															</button>
														)}
													</div>
												);
											})}
										</div>
									)}
								</div>
							);
						},
					)}

					{/* Loading more indicator */}
					{isSelfFetching && isLoadingMore && (
						<div className="flex items-center justify-center p-4">
							<FontAwesomeIcon
								icon={faSpinner}
								className={cn(
									'w-4 h-4 animate-spin mr-2',
									colors.classes.textSecondary,
								)}
							/>
							<Text size="sm" className={colors.classes.textSecondary}>
								{t('Loading more tools...')}
							</Text>
						</div>
					)}

					{/* End of list indicator */}
					{isSelfFetching && !hasMore && availableTools.length > 0 && (
						<div className="flex items-center justify-center p-4">
							<Text size="sm" className={colors.classes.textSecondary}>
								{t('All tools loaded')}
							</Text>
						</div>
					)}
				</div>

				{/* Selection summary */}
				<div
					className={cn(
						'p-3 border-t',
						colors.classes.borderDefault,
						colors.classes.bgSecondary,
					)}
				>
					<Text size="sm" className={colors.classes.textSecondary}>
						{t('{count} tools selected').replace(
							'{count}',
							String(selectedTools.length),
						)}
					</Text>
				</div>
			</div>

			{/* Right Panel: Configuration */}
			{configuringTool != null && (
				<div className="w-1/2 flex flex-col min-w-0 overflow-hidden">
					<div
						className={cn(
							'p-3 border-b flex items-center justify-between',
							colors.classes.borderDefault,
							colors.classes.bgSecondary,
						)}
					>
						<Text weight="medium" className={colors.classes.textPrimary}>
							{t('Configure: {name}').replace('{name}', configuringTool.name)}
						</Text>
						<button
							type="button"
							onClick={() => {
								setConfiguringToolId(null);
							}}
							className={cn(
								'text-sm',
								colors.classes.textSecondary,
								'hover:text-strong',
							)}
						>
							{t('Close')}
						</button>
					</div>
					<div className="flex-1 overflow-y-auto p-4 space-y-4">
						{(() => {
							const agentConfig = getAgentConfig(configuringTool);

							if (agentConfig == null) {
								return (
									<Text className={colors.classes.textSecondary}>
										{t('No configuration required for this tool.')}
									</Text>
								);
							}

							const currentConfig = selectedToolsMap.get(configuringTool.id);

							return Object.entries(agentConfig).map(
								([fieldName, fieldDef]) => (
									<AgentToolConfigField
										key={fieldName}
										fieldName={fieldName}
										fieldDefinition={fieldDef}
										value={currentConfig?.[fieldName]}
										onChange={(value) => {
											handleConfigChange(configuringTool.id, fieldName, value);
										}}
									/>
								),
							);
						})()}
					</div>
				</div>
			)}
		</div>
	);
};

export default AgentToolSelector;
